import { Button, cn, Icon, type MaterialSymbol, Tooltip, TooltipContent, TooltipTrigger } from "@qw/ui";
import { type FC, type FocusEvent, type KeyboardEvent, type MouseEvent, type PointerEvent, useState } from "react";
import type { ConversationMenuItem, ConversationMenuItemId } from "@/providers/use-conversation-menu";

interface ConversationLinkActionsProps {
  isKeyboardActive?: boolean;
  menuItems: ConversationMenuItem[];
}

interface ConversationLinkAction {
  id: string;
  icon: MaterialSymbol;
  label: string;
  iconClassName?: string;
  disabled?: boolean;
  onClick: () => void;
}

type ButtonConversationMenuItem = Extract<ConversationMenuItem, { type: "button" }>;

const getButtonMenuItem = (menuItems: ConversationMenuItem[], id: ConversationMenuItemId) =>
  menuItems.find((item): item is ButtonConversationMenuItem => item.type === "button" && item.id === id);

export const ConversationLinkActions: FC<ConversationLinkActionsProps> = ({ isKeyboardActive, menuItems }) => {
  const [hasFocusWithin, setHasFocusWithin] = useState(false);

  const replyAllItem = getButtonMenuItem(menuItems, "reply-all");
  const forwardItem = getButtonMenuItem(menuItems, "forward");
  const starItem = getButtonMenuItem(menuItems, "star");
  const inboxItem = getButtonMenuItem(menuItems, "inbox");
  const unreadItem = getButtonMenuItem(menuItems, "read-state");

  const actions = [
    replyAllItem && {
      id: "reply-all",
      icon: replyAllItem.icon,
      label: replyAllItem.label,
      disabled: replyAllItem.disabled,
      onClick: replyAllItem.onClick,
    },
    forwardItem && {
      id: "forward",
      icon: forwardItem.icon,
      label: forwardItem.label,
      disabled: forwardItem.disabled,
      onClick: forwardItem.onClick,
    },
    starItem && {
      id: "star",
      icon: starItem.icon,
      label: starItem.label,
      // Key the gold fill off the "filled" marker the menu sets on the active-star state,
      // not the display label, so copy/i18n changes don't silently drop the styling.
      iconClassName: cn(
        starItem.iconProps?.className,
        starItem.iconProps?.className?.includes("filled") && "text-yellow-500"
      ),
      disabled: starItem.disabled,
      onClick: starItem.onClick,
    },
    inboxItem && {
      id: "inbox",
      // The menu uses "inventory_2" for the archive action; swap in the strip's preferred
      // "archive" glyph by matching that icon constant rather than the display label.
      icon: inboxItem.icon === "inventory_2" ? ("archive" as const) : inboxItem.icon,
      label: inboxItem.label,
      disabled: inboxItem.disabled,
      onClick: inboxItem.onClick,
    },
    unreadItem && {
      id: "read-state",
      icon: unreadItem.icon,
      label: unreadItem.label,
      disabled: unreadItem.disabled,
      onClick: unreadItem.onClick,
    },
  ].filter(Boolean) as ConversationLinkAction[];

  if (!actions.length) {
    return null;
  }

  const handleKeyUp = (event: KeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocusTarget = event.relatedTarget as Node | null;

    if (nextFocusTarget && event.currentTarget.contains(nextFocusTarget)) {
      return;
    }

    setHasFocusWithin(false);
  };
  const isAccessible = isKeyboardActive || hasFocusWithin;

  return (
    <div
      data-testid="conversation-link-actions"
      aria-hidden={isAccessible ? undefined : true}
      onBlur={handleBlur}
      onFocus={() => setHasFocusWithin(true)}
      onPointerDown={(event: PointerEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
      onMouseDown={(event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
      className={cn(
        "pointer-events-none absolute right-1.5 top-1/2 z-10 flex -translate-y-1/2 items-center gap-0.5 rounded-sm bg-background/95 p-0.5 text-foreground opacity-0 shadow-sm ring-1 ring-border/50 transition-opacity",
        "group-hover/conversation-link:pointer-events-auto group-hover/conversation-link:opacity-100 group-focus-within/conversation-link:pointer-events-auto group-focus-within/conversation-link:opacity-100"
      )}
    >
      {actions.map(({ id, icon, iconClassName, label, disabled, onClick }) => (
        <Tooltip key={id}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              data-conversation-link-action={id}
              variant="ghost"
              display="icon"
              size="xs"
              aria-label={label}
              disabled={disabled}
              tabIndex={isAccessible && !disabled ? 0 : -1}
              className="relative z-10 text-inherit hover:text-inherit"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                if (disabled) {
                  return;
                }
                onClick();
              }}
              onKeyUp={handleKeyUp}
            >
              <Icon icon={icon} className={iconClassName} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
};
